/**
 * PaymentReturn (Sprint 5 B6) — landing page sau khi user redirect về từ PayOS.
 *
 * URL patterns:
 *   - /payment/success?orderId=X  — PayOS redirect khi pay OK (mock thì auto)
 *   - /payment/cancel?orderId=X   — PayOS redirect khi user cancel
 *
 * Behavior:
 *   - Poll order status mỗi 2s (max 30s) cho đến khi PAID/CANCELLED/FAILED
 *   - PAID → show success + button "Vào môn học"
 *   - CANCELLED → show info "Đã huỷ"
 *   - Timeout 30s vẫn PENDING → show warning "Đang xử lý, kiểm tra lại sau"
 */

import { useEffect, useState } from "react";
import { Button, Card, Result, Spin } from "antd";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/api/client";
import type { Order } from "@/api/payment";

const POLL_INTERVAL_MS = 2000;
const POLL_TIMEOUT_MS = 30_000;

type ResultMode = "loading" | "paid" | "cancelled" | "timeout" | "error";

export function PaymentReturn() {
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const orderId = params.get("orderId");
  const isCancelPath = location.pathname.endsWith("/cancel");

  const [mode, setMode] = useState<ResultMode>(
    isCancelPath ? "cancelled" : "loading",
  );
  const [order, setOrder] = useState<Order | null>(null);

  useEffect(() => {
    if (!orderId || isCancelPath) return;
    let cancelled = false;
    const start = Date.now();

    const poll = async () => {
      if (cancelled) return;
      try {
        // Lấy single order qua /me/ list rồi filter (BE chưa có /orders/:id GET)
        const resp = await apiClient.get<{ results: Order[] }>(
          "/payment/me/orders/",
        );
        const found = resp.data.results.find((o) => String(o.id) === orderId);
        if (!found) {
          setMode("error");
          return;
        }
        setOrder(found);
        if (found.status === "paid") {
          setMode("paid");
          // Invalidate enrollment cache để CourseDetail thấy enrollment mới
          qc.invalidateQueries({ queryKey: ["enrollment"] });
          return;
        }
        if (found.status === "cancelled" || found.status === "failed") {
          setMode("cancelled");
          return;
        }
        // Vẫn pending → poll lại sau 2s nếu chưa timeout
        if (Date.now() - start > POLL_TIMEOUT_MS) {
          setMode("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (!cancelled) setMode("error");
      }
    };
    poll();

    return () => {
      cancelled = true;
    };
  }, [orderId, isCancelPath, qc]);

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f4f5f7",
      }}
    >
      <Card style={{ width: 520, padding: "16px 8px" }}>
        {mode === "loading" && (
          <div style={{ textAlign: "center", padding: 32 }} data-testid="payment-loading">
            <Spin size="large" />
            <div style={{ marginTop: 16, color: "#7a8499" }}>
              Đang xác nhận thanh toán...
            </div>
            <div style={{ marginTop: 8, fontSize: 12, color: "#9ca3af" }}>
              Vui lòng không đóng trang. Tự động kiểm tra mỗi 2 giây.
            </div>
          </div>
        )}

        {mode === "paid" && order && (
          <Result
            status="success"
            title="Thanh toán thành công!"
            subTitle={
              order.course ? (
                <>
                  Đơn hàng <strong>#{order.id}</strong> ({order.amount_display}) đã
                  được xác nhận. Bạn đã được đăng ký khóa{" "}
                  <strong>{order.course.name}</strong>.
                </>
              ) : (
                <>
                  Đơn hàng <strong>#{order.id}</strong> ({order.amount_display}) đã
                  được xác nhận. Bạn đã được đăng ký{" "}
                  <strong>{order.items.length} môn học</strong>.
                </>
              )
            }
            extra={[
              <Link to={order.course ? `/courses/${order.course.slug}` : "/my-courses"} key="course">
                <Button type="primary" size="large">
                  Vào môn học
                </Button>
              </Link>,
              <Link to="/my-orders" key="orders">
                <Button>Xem đơn hàng</Button>
              </Link>,
            ]}
          />
        )}

        {mode === "cancelled" && (
          <Result
            status="warning"
            title="Đã huỷ thanh toán"
            subTitle="Bạn đã huỷ hoặc đóng cửa sổ thanh toán. Có thể đăng ký lại bất cứ lúc nào."
            extra={[
              <Button
                type="primary"
                key="back"
                onClick={() => navigate("/courses")}
              >
                Quay lại danh sách môn học
              </Button>,
            ]}
          />
        )}

        {mode === "timeout" && (
          <Result
            status="info"
            title="Đang xử lý"
            subTitle="Hệ thống đang xác nhận thanh toán với ngân hàng. Vui lòng kiểm tra lại sau vài phút."
            extra={[
              <Link to="/my-orders" key="orders">
                <Button type="primary">Xem đơn hàng</Button>
              </Link>,
            ]}
          />
        )}

        {mode === "error" && (
          <Result
            status="error"
            title="Không tìm thấy đơn hàng"
            subTitle="Đơn hàng không tồn tại hoặc không thuộc tài khoản của bạn."
            extra={[
              <Link to="/my-orders" key="orders">
                <Button type="primary">Xem đơn hàng của tôi</Button>
              </Link>,
            ]}
          />
        )}
      </Card>
    </div>
  );
}

export default PaymentReturn;
